<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useTimeAgo } from 'dashboard/composables/useTimeAgo';

import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  contacts: {
    type: Array,
    required: true,
  },
  selectedContactIds: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['toggleContact', 'toggleSelectAll', 'showContact']);

const { t } = useI18n();
const { timeAgo } = useTimeAgo();

const tableHeaders = computed(() => [
  '',
  t('CONTACTS_LAYOUT.TABLE.COLUMNS.NAME'),
  t('CONTACTS_LAYOUT.TABLE.COLUMNS.EMAIL'),
  t('CONTACTS_LAYOUT.TABLE.COLUMNS.PHONE'),
  t('CONTACTS_LAYOUT.TABLE.COLUMNS.COMPANY'),
  t('CONTACTS_LAYOUT.TABLE.COLUMNS.CITY'),
  t('CONTACTS_LAYOUT.TABLE.COLUMNS.LAST_ACTIVITY'),
  t('CONTACTS_LAYOUT.TABLE.COLUMNS.CREATED_AT'),
  '',
]);

const selectedIdsSet = computed(() => new Set(props.selectedContactIds || []));

const isSelected = id => selectedIdsSet.value.has(id);

const allSelected = computed(
  () =>
    props.contacts.length > 0 &&
    props.contacts.every(contact => isSelected(contact.id))
);

const isIndeterminate = computed(
  () =>
    !allSelected.value && props.contacts.some(contact => isSelected(contact.id))
);

const formatTime = value => timeAgo(value) || '-';

const toggleSelectAll = () => emit('toggleSelectAll', !allSelected.value);

const toggleContact = (id, event) =>
  emit('toggleContact', { id, value: event.target.checked });
</script>

<template>
  <BaseTable
    :headers="tableHeaders"
    :items="contacts"
    class="[&_th]:whitespace-nowrap isolate"
  >
    <template #header-0>
      <Checkbox
        :model-value="allSelected"
        :indeterminate="isIndeterminate"
        :aria-label="t('CONTACTS_LAYOUT.TABLE.SELECT_ALL')"
        @change="toggleSelectAll"
      />
    </template>
    <template #row="{ items }">
      <BaseTableRow
        v-for="contact in items"
        :key="contact.id"
        :item="contact"
        class="cursor-pointer hover:bg-n-alpha-1"
        :class="{ 'bg-n-alpha-1': isSelected(contact.id) }"
        @click="emit('showContact', contact.id)"
      >
        <template #default>
          <BaseTableCell class="w-10" @click.stop>
            <Checkbox
              :model-value="isSelected(contact.id)"
              :aria-label="
                t('CONTACTS_LAYOUT.TABLE.SELECT_ROW', { name: contact.name })
              "
              @change="event => toggleContact(contact.id, event)"
            />
          </BaseTableCell>

          <BaseTableCell>
            <div class="flex items-center min-w-0 gap-2.5">
              <Avatar
                :src="contact.thumbnail || ''"
                :name="contact.name || ''"
                :size="28"
                :status="contact.availabilityStatus"
                hide-offline-status
              />
              <span class="font-medium truncate text-n-slate-12">
                {{ contact.name }}
              </span>
            </div>
          </BaseTableCell>

          <BaseTableCell>
            <div v-if="contact.email" class="flex items-center min-w-0 gap-1.5">
              <Icon
                icon="i-lucide-mail"
                class="flex-shrink-0 size-3.5 text-n-slate-10"
              />
              <span class="truncate text-n-slate-11">{{ contact.email }}</span>
            </div>
            <span v-else class="text-n-slate-10">-</span>
          </BaseTableCell>

          <BaseTableCell>
            <div
              v-if="contact.phoneNumber"
              class="flex items-center min-w-0 gap-1.5"
            >
              <Icon
                icon="i-lucide-phone"
                class="flex-shrink-0 size-3.5 text-n-slate-10"
              />
              <span class="truncate text-n-slate-11">{{
                contact.phoneNumber
              }}</span>
            </div>
            <span v-else class="text-n-slate-10">-</span>
          </BaseTableCell>

          <BaseTableCell>
            <span class="truncate text-n-slate-11">
              {{ contact.additionalAttributes?.companyName || '-' }}
            </span>
          </BaseTableCell>

          <BaseTableCell>
            <span class="truncate text-n-slate-11">
              {{ contact.additionalAttributes?.city || '-' }}
            </span>
          </BaseTableCell>

          <BaseTableCell>
            <span class="whitespace-nowrap text-n-slate-11">
              {{ formatTime(contact.lastActivityAt) }}
            </span>
          </BaseTableCell>

          <BaseTableCell>
            <span class="whitespace-nowrap text-n-slate-11">
              {{ formatTime(contact.createdAt) }}
            </span>
          </BaseTableCell>

          <BaseTableCell align="end" class="w-12" @click.stop>
            <Button
              v-tooltip.top="t('CONTACTS_LAYOUT.TABLE.ACTIONS.EDIT')"
              ghost
              slate
              sm
              icon="i-lucide-pencil"
              :aria-label="t('CONTACTS_LAYOUT.TABLE.ACTIONS.EDIT')"
              @click="emit('showContact', contact.id)"
            />
          </BaseTableCell>
        </template>
      </BaseTableRow>
    </template>
  </BaseTable>
</template>
